/**
 * FE mirror of the backend leg codec:
 * sky360-ms/apps/search-engine/src/common/utils/leg-codec.ts
 *
 * Must stay byte-compatible with the backend on valid input — same separators
 * (`_` between legs, `.` between fields), same `YYYY-MM-DD` wire form, same
 * `DD/MM/YYYY` decoded form, same real-calendar rejection (2026-02-31 must
 * fail, not roll over to March), same 2..5 leg bounds, same IATA upper-casing.
 *
 * Deliberate divergence: the backend throws `BadRequestException` on anything
 * malformed. This module never throws — it returns `[]` instead. A user can
 * hand-edit the URL, and the results page must degrade to its empty state
 * rather than crash.
 *
 * Worked example (same one used in leg-codec.spec.ts on the BE — verified
 * byte-identical against that spec):
 *   encodeLegs([
 *     { departure: 'TLV', destination: 'MXP', date: '01/09/2026' },
 *     { departure: 'MXP', destination: 'TBS', date: '05/09/2026' },
 *     { departure: 'KUT', destination: 'TLV', date: '10/09/2026' },
 *   ]) === 'TLV.MXP.2026-09-01_MXP.TBS.2026-09-05_KUT.TLV.2026-09-10'
 *
 *   decodeLegs('TLV.MXP.2026-09-01_MXP.TBS.2026-09-05_KUT.TLV.2026-09-10') === [
 *     { departure: 'TLV', destination: 'MXP', date: '01/09/2026' },
 *     { departure: 'MXP', destination: 'TBS', date: '05/09/2026' },
 *     { departure: 'KUT', destination: 'TLV', date: '10/09/2026' },
 *   ]
 */

export interface MultiCityLeg {
  departure: string;
  destination: string;
  date: string; /* DD/MM/YYYY */
}

export const MIN_LEGS = 2;
export const MAX_LEGS = 5;

/**
 * The form `MultiCityLeg.date` is in — this codec's OWN contract, pinned as a
 * literal.
 *
 * Deliberately NOT `siteConfig.dateFormat`: that is a per-tenant DISPLAY format
 * and it is not guaranteed to be 'DD/MM/YYYY'. A tenant on 'MM/DD/YYYY' would
 * silently swap day and month in every leg it encoded, decoded or rendered.
 *
 * Exported so every consumer that has to parse or print a leg date reads the
 * value from the module that defines it, rather than re-declaring the literal
 * and drifting from `DDMMYYYY` below.
 */
export const LEG_DATE_FORMAT = 'DD/MM/YYYY';

const LEG_SEP = '_';
const FIELD_SEP = '.';
const IATA = /^[A-Z]{3}$/;
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const DDMMYYYY = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** '01/09/2026' -> '2026-09-01', or null if malformed */
function toIso(ddmmyyyy: string): string | null {
  const m = DDMMYYYY.exec(ddmmyyyy);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
}

/** '2026-09-01' -> '01/09/2026', rejecting impossible calendar dates */
function fromIso(iso: string): string | null {
  const m = ISO.exec(iso);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    d.getFullYear() !== Number(yyyy) ||
    d.getMonth() !== Number(mm) - 1 ||
    d.getDate() !== Number(dd)
  ) {
    return null;
  }
  return `${dd}/${mm}/${yyyy}`;
}

export function encodeLegs(legs: MultiCityLeg[]): string {
  return legs
    .map((l) => {
      const iso = toIso(l.date);
      if (!iso) {
        // Deliberate divergence from decodeLegs' "never throw" rule above:
        // decodeLegs parses untrusted URL text, so it degrades to []. This
        // function only ever receives internally-built MultiCityLeg objects
        // (every caller hands it a valid Moment-formatted date) — a bad date
        // here is a caller bug, not user input. Emitting the old `iso ?? ''`
        // silently produced a corrupted wire string (empty date field) that
        // the backend codec then throws on decoding; failing fast here, at
        // the point of encoding, mirrors that backend fail-fast instead of
        // pushing a broken URL out into the world.
        throw new Error(
          `encodeLegs: invalid leg date "${l.date}" for ${l.departure}->${l.destination}`
        );
      }
      return [l.departure.toUpperCase(), l.destination.toUpperCase(), iso].join(FIELD_SEP);
    })
    .join(LEG_SEP);
}

export function decodeLegs(raw: string | null): MultiCityLeg[] {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  const parts = raw.split(LEG_SEP).filter((p) => p.length > 0);
  if (parts.length < MIN_LEGS || parts.length > MAX_LEGS) {
    return [];
  }

  const legs: MultiCityLeg[] = [];
  for (const part of parts) {
    const fields = part.split(FIELD_SEP);
    if (fields.length !== 3) {
      return [];
    }
    const [dep, dest, iso] = fields;
    const departure = dep.toUpperCase();
    const destination = dest.toUpperCase();
    if (!IATA.test(departure) || !IATA.test(destination)) {
      return [];
    }
    if (departure === destination) {
      return [];
    }
    const date = fromIso(iso);
    if (!date) {
      return [];
    }
    legs.push({ departure, destination, date });
  }
  return legs;
}

const MS_PER_DAY = 86400000;

/** A DD/MM/YYYY date as a whole UTC day number, or null when it is not one. */
function toDayNumber(ddmmyyyy: string | null | undefined): number | null {
  if (!ddmmyyyy) return null;
  const iso = toIso(ddmmyyyy);
  if (!iso || !fromIso(iso)) return null;
  const [yyyy, mm, dd] = iso.split('-').map(Number);
  return Date.UTC(yyyy, mm - 1, dd) / MS_PER_DAY;
}

function fromDayNumber(day: number): string {
  const d = new Date(day * MS_PER_DAY);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/**
 * Moves ONE leg's searched date by `deltaDays`, for the ‹ › day stepper on the
 * multi-city results header. Returns the new legs, or null when the move is not
 * allowed:
 *  - before `today` (DD/MM/YYYY, supplied by the caller so this stays pure);
 *  - before the previous leg's date or after the next leg's date — legs stay
 *    in non-decreasing date order, the same rule the multi-city search form
 *    enforces (`getMinDateForIndex`). Two legs on the same day are allowed.
 *
 * Neighbouring legs are never moved: shifting one leg must not silently
 * re-date the rest of the trip.
 */
export function shiftLegDate(
  legs: MultiCityLeg[],
  index: number,
  deltaDays: number,
  today?: string
): MultiCityLeg[] | null {
  if (!Array.isArray(legs) || index < 0 || index >= legs.length) return null;
  if (!Number.isInteger(deltaDays) || deltaDays === 0) return null;

  const current = toDayNumber(legs[index]?.date);
  if (current === null) return null;
  const target = current + deltaDays;

  const todayDay = toDayNumber(today ?? null);
  if (todayDay !== null && target < todayDay) return null;

  const previous = index > 0 ? toDayNumber(legs[index - 1]?.date) : null;
  if (previous !== null && target < previous) return null;

  const next = index < legs.length - 1 ? toDayNumber(legs[index + 1]?.date) : null;
  if (next !== null && target > next) return null;

  return legs.map((leg, i) =>
    i === index ? { ...leg, date: fromDayNumber(target) } : leg
  );
}
